import { appState } from "./state";
import { notifyStateChange } from "./stateBus";
import { apiFetch } from "./api";
import { requestLock } from "./lockBus";
import { showToast } from "./toast";
import type { PartySession } from "./types";
import type {
  PartyAcceptedData,
  PartyDeclinedData,
  PartyEndedData,
  PartyInviteData,
  PartyQueueChangedData,
  PartyReactionData,
} from "./chatSocket";

// Framework-agnostic Party Room data layer, same shape as togetherApi.ts -
// party_sync is deliberately NOT handled here: applying a remote sync
// touches the shared #audioPlayer element directly, which belongs in
// partySync.ts (built alongside the audio-player integration), not this
// framework-agnostic data layer.

export type PartyResult<T extends object = { session?: never }> = ({ ok: true } & T) | { ok: false; error?: string };

export async function invitePartyApi(params: { recipientEmail?: string; filename?: string }): Promise<PartyResult<{ session: PartySession }>> {
  const res = await apiFetch("/api/party/invite", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) return { ok: false, error: data.message || data.error };

  appState.partySession = data.session as PartySession;
  notifyStateChange();
  return { ok: true, session: data.session };
}

export async function acceptPartyApi(sessionId: string): Promise<PartyResult<{ session: PartySession }>> {
  const res = await apiFetch(`/api/party/${sessionId}/accept`, { method: "POST" });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) return { ok: false, error: data.message || data.error };

  appState.partyIncomingInvite = null;
  appState.partySession = data.session as PartySession;
  notifyStateChange();
  return { ok: true, session: data.session };
}

export async function declinePartyApi(sessionId: string): Promise<PartyResult> {
  const res = await apiFetch(`/api/party/${sessionId}/decline`, { method: "POST" });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    return { ok: false, error: data.message || data.error };
  }

  appState.partyIncomingInvite = null;
  notifyStateChange();
  return { ok: true };
}

export async function addToPartyQueueApi(sessionId: string, filename: string): Promise<PartyResult<{ session: PartySession }>> {
  const res = await apiFetch(`/api/party/${sessionId}/queue/add`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ filename }),
  });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) return { ok: false, error: data.message || data.error };

  appState.partySession = data.session as PartySession;
  notifyStateChange();
  return { ok: true, session: data.session };
}

export async function removeFromPartyQueueApi(sessionId: string, queueId: string): Promise<PartyResult<{ session: PartySession }>> {
  const res = await apiFetch(`/api/party/${sessionId}/queue/remove`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ queueId }),
  });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) return { ok: false, error: data.message || data.error };

  appState.partySession = data.session as PartySession;
  notifyStateChange();
  return { ok: true, session: data.session };
}

// Fire-and-forget from the caller's perspective (partySync.ts throttles
// calls itself) - a 429 here just means "try again next tick," not a real
// error.
export async function syncPartyApi(
  sessionId: string,
  params: { currentIndex: number; position: number; playing: boolean },
): Promise<PartyResult> {
  const res = await apiFetch(`/api/party/${sessionId}/sync`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  if (!res.ok) return { ok: false };
  return { ok: true };
}

// Renders the sender's own burst immediately (optimistic), same as
// reactTogetherApi - the server only ever pushes party_reaction to the
// OTHER participant.
export async function reactPartyApi(sessionId: string, emoji: string): Promise<PartyResult> {
  appState.partyReactions = [...appState.partyReactions, { id: crypto.randomUUID(), emoji, fromUserId: appState.currentUserEmail }];
  notifyStateChange();

  const res = await apiFetch(`/api/party/${sessionId}/react`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ emoji }),
  });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  if (!res.ok) return { ok: false };
  return { ok: true };
}

export async function fetchPartySessionApi(): Promise<PartySession | null> {
  const res = await apiFetch("/api/party/session");
  if (res.status === 401) {
    requestLock("unauthorized");
    return null;
  }
  if (!res.ok) return null;
  const data = await res.json().catch(() => ({}));
  return (data.session as PartySession | null) ?? null;
}

export async function leavePartyApi(sessionId: string): Promise<PartyResult> {
  const res = await apiFetch(`/api/party/${sessionId}/leave`, { method: "POST" });
  if (res.status === 401) {
    requestLock("unauthorized");
    return { ok: false };
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    return { ok: false, error: data.message || data.error };
  }

  appState.partySession = null;
  notifyStateChange();
  return { ok: true };
}

export function removePartyReaction(id: string): void {
  appState.partyReactions = appState.partyReactions.filter((r) => r.id !== id);
  notifyStateChange();
}

// --- Apply incoming WS pushes to appState (called from the socket handler
// registry PartyRoot.tsx sets up via setPartySocketHandlers) ---

export function applyPartyInvite(data: PartyInviteData): void {
  if (appState.partySession) return;
  appState.partyIncomingInvite = data;
  notifyStateChange();
}

export function applyPartyAccepted(data: PartyAcceptedData): void {
  if (!appState.partySession || appState.partySession.id !== data.sessionId) return;
  appState.partySession = data.session as PartySession;
  notifyStateChange();
}

export function applyPartyDeclined(data: PartyDeclinedData): void {
  if (!appState.partySession || appState.partySession.id !== data.sessionId) return;
  showToast(`${appState.partySession.peerName} declined your invite.`, "info");
  appState.partySession = null;
  notifyStateChange();
}

export function applyPartyQueueChanged(data: PartyQueueChangedData): void {
  if (!appState.partySession || appState.partySession.id !== data.sessionId) return;
  appState.partySession = { ...appState.partySession, queue: data.queue, currentIndex: data.currentIndex };
  notifyStateChange();
}

export function applyPartyEnded(data: PartyEndedData): void {
  if (!appState.partySession || appState.partySession.id !== data.sessionId) return;
  if (data.reason === "disconnected") showToast(`${appState.partySession.peerName} disconnected.`, "info");
  else if (data.reason === "expired") showToast("Your invite expired.", "info");
  appState.partySession = null;
  notifyStateChange();
}

export function applyPartyReaction(data: PartyReactionData): void {
  if (!appState.partySession || appState.partySession.id !== data.sessionId) return;
  appState.partyReactions = [...appState.partyReactions, { id: crypto.randomUUID(), emoji: data.emoji, fromUserId: data.fromUserId }];
  notifyStateChange();
}
